import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/axios';
import CModal from '../shared/c-modal';
import Dropdown from '../Dropdown';
import {
  DOCUMENT_CATEGORIES, MAX_DOCUMENT_BYTES, documentAcceptFor, documentKindsTextFor,
} from '../../constants';

const INPUT = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500';
const MAX_MB = MAX_DOCUMENT_BYTES / (1024 * 1024);

/**
 * Upload one file into an employee's documents folder (POST /api/documents, multipart).
 * The category is one of the fixed list, and the file picker offers what that category
 * takes; the size is checked here too so a 40 MB scan fails before it is sent. The server
 * judges the file's bytes and is the authority on both. An employee's own upload waits for
 * HR approval; an HR upload on someone's behalf is filed approved (`autoApproved`).
 */
const UploadDocumentModal = ({ employeeId, employeeName, onBehalf, autoApproved, onClose, onUploaded }) => {
  const fileRef = useRef(null);
  const [category, setCategory] = useState('');
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const pickCategory = (value) => {
    setCategory(value);
    setError('');
    // A file chosen for another category may not fit this one: choose again.
    if (fileRef.current) fileRef.current.value = '';
    setFile(null);
  };

  const pickFile = (event) => {
    const chosen = event.target.files?.[0] || null;
    setError(chosen && chosen.size > MAX_DOCUMENT_BYTES ? `The file is larger than ${MAX_MB} MB.` : '');
    setFile(chosen);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!category) { setError('Choose a category.'); return; }
    if (!file) { setError('Choose a file.'); return; }
    if (file.size > MAX_DOCUMENT_BYTES) { setError(`The file is larger than ${MAX_MB} MB.`); return; }

    const form = new FormData();
    form.append('employee_id', String(employeeId));
    form.append('document_type', category);
    if (title.trim()) form.append('title', title.trim());
    if (expiryDate) form.append('expiry_date', expiryDate);
    form.append('file', file);

    setUploading(true);
    try {
      await api.post('/api/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(autoApproved ? 'Document uploaded' : 'Document uploaded and sent to HR for approval');
      onUploaded();
    } catch (err) {
      const body = err.response?.data;
      setError(body?.message || body?.errors?.[0]?.msg || 'The upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <CModal
      onClose={onClose}
      labelledBy="upload-document-title"
      dismissible={!uploading}
      closeOnBackdrop={false}
      overlayClassName="bg-black/40"
      className="bg-white rounded-lg shadow-xl w-full max-w-md"
      data-testid="employee-documents-upload-modal"
    >
      <form onSubmit={submit} noValidate>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 id="upload-document-title" className="text-base font-semibold text-gray-900">Upload document</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {onBehalf
              ? `For ${employeeName || 'this employee'}. ${autoApproved ? 'Filed as approved.' : 'It waits for approval.'}`
              : 'HR reviews every upload before it counts.'}
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="upload-document-category" className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-danger-600">*</span>
            </label>
            <Dropdown
              id="upload-document-category"
              data-testid="employee-documents-upload-category"
              value={category}
              onChange={(e) => pickCategory(e.target.value)}
              className={INPUT}
            >
              <option value="">Choose a category</option>
              {DOCUMENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Dropdown>
          </div>

          <div>
            <label htmlFor="upload-document-file" className="block text-sm font-medium text-gray-700 mb-1">
              File <span className="text-danger-600">*</span>
            </label>
            <input
              id="upload-document-file"
              data-testid="employee-documents-upload-file"
              ref={fileRef}
              type="file"
              accept={documentAcceptFor(category)}
              onChange={pickFile}
              disabled={!category}
              aria-describedby="upload-document-file-hint"
              className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 file:mr-3 file:border-0 file:border-r file:border-gray-300 file:bg-gray-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-100"
            />
            <p id="upload-document-file-hint" className="mt-1 text-xs text-gray-500">
              {category ? `${documentKindsTextFor(category)}, up to ${MAX_MB} MB.` : 'Choose a category first.'}
            </p>
          </div>

          <div>
            <label htmlFor="upload-document-title-input" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="upload-document-title-input"
              data-testid="employee-documents-upload-title"
              type="text"
              value={title}
              maxLength={255}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={category || 'Defaults to the category'}
              className={INPUT}
            />
          </div>

          <div>
            <label htmlFor="upload-document-expiry" className="block text-sm font-medium text-gray-700 mb-1">
              Expiry date <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="upload-document-expiry"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className={INPUT}
            />
          </div>

          {error && (
            <p role="alert" data-testid="employee-documents-upload-error" className="text-sm text-danger-700">{error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="employee-documents-upload-submit"
            disabled={uploading}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-60"
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
    </CModal>
  );
};

export default UploadDocumentModal;
